/**
 * Interface for address data that can be used with the formatter
 * This allows the formatter to work with different address data structures
 */
export interface AddressData {
  houseUnitNo?: string;
  streetName?: string;
  subdivisionVillage?: string;
  barangay?: string;
  city?: string;
  province?: string;
  zipCode?: string;
  country?: string;
}

/**
 * Formats address data into a complete, readable address string
 * Follows Philippine address format: House/Unit, Street, Subdivision/Village, Barangay, City, Province, ZIP, Country
 * 
 * @param addressData - The address data object
 * @param options - Formatting options
 * @returns Formatted address string
 */
export const formatCompleteAddress = (
  addressData: AddressData | null | undefined,
  options: {
    includeCountry?: boolean;
    includeZipCode?: boolean;
    separator?: string;
  } = {}
): string => {
  if (!addressData) {
    return '';
  }

  const {
    includeCountry = true,
    includeZipCode = true,
    separator = ', '
  } = options;

  const addressParts: string[] = [];

  // House/Unit No. and Street Name (combined as first part)
  const streetAddress = [
    addressData.houseUnitNo?.trim(),
    addressData.streetName?.trim()
  ].filter(Boolean).join(' ');
  
  if (streetAddress) {
    addressParts.push(streetAddress);
  }

  // Subdivision/Village
  if (addressData.subdivisionVillage?.trim()) {
    addressParts.push(addressData.subdivisionVillage.trim());
  }

  // Barangay
  if (addressData.barangay?.trim()) {
    addressParts.push(addressData.barangay.trim());
  }

  // City
  if (addressData.city?.trim()) {
    addressParts.push(addressData.city.trim());
  }

  // Province
  if (addressData.province?.trim()) {
    addressParts.push(addressData.province.trim());
  }

  // ZIP Code
  if (includeZipCode && addressData.zipCode?.trim()) {
    addressParts.push(addressData.zipCode.trim());
  }

  // Country
  if (includeCountry && addressData.country?.trim()) {
    addressParts.push(addressData.country.trim());
  }

  return addressParts.join(separator);
};

/**
 * Formats address data for display in summary sections
 * Uses the complete format with all fields
 * 
 * @param addressData - The address data object
 * @returns Formatted address string for summary display
 */
export const formatAddressForSummary = (addressData: AddressData | null | undefined): string => {
  return formatCompleteAddress(addressData, {
    includeCountry: true,
    includeZipCode: true,
    separator: ', '
  });
};

/**
 * Formats address data for compact display (without country and zip)
 * Useful for cards or limited space displays
 * 
 * @param addressData - The address data object
 * @returns Formatted address string for compact display
 */
export const formatAddressCompact = (addressData: AddressData | null | undefined): string => {
  return formatCompleteAddress(addressData, {
    includeCountry: false,
    includeZipCode: false,
    separator: ', '
  });
};

/**
 * Legacy function for backward compatibility
 * Formats address using only house/unit, street, and city (old format)
 * 
 * @param addressData - The address data object
 * @returns Formatted address string in legacy format
 * @deprecated Use formatCompleteAddress or formatAddressForSummary instead
 */
export const formatAddressLegacy = (addressData: AddressData | null | undefined): string => {
  if (!addressData) {
    return '';
  }

  const parts = [
    addressData.houseUnitNo?.trim(),
    addressData.streetName?.trim()
  ].filter(Boolean);

  const streetAddress = parts.join(' ');
  const city = addressData.city?.trim();

  if (streetAddress && city) {
    return `${streetAddress}, ${city}`;
  } else if (streetAddress) {
    return streetAddress;
  } else if (city) {
    return city;
  }

  return '';
};